'use client'

import { useEffect } from 'react'

/**
 * Next.js root-level error boundary. Replaces the default "Something went
 * wrong" page with one that logs the full error to the browser console
 * AND POSTs it to /api/__diagnostics/client-error so the server log captures
 * the readable stack (source maps now enabled, so it points to real files).
 *
 * Without this, server-rendered errors like `Element type is invalid` show
 * up in pm2 err log as a minified react-dom-server stack with no component
 * name — undebuggable. With this, the next time it fires we'll see exactly
 * which component is undefined.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log everything we can to the browser console for the operator
    // eslint-disable-next-line no-console
    console.error('[Aether global-error]', {
      message: error.message,
      digest: error.digest,
      stack: error.stack,
      name: error.name,
    })

    // Also POST to a diagnostic endpoint so it lands in the pm2 out log,
    // where operators can correlate against agent activity. Fire-and-forget.
    void fetch('/api/__diagnostics/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        at: new Date().toISOString(),
      }),
    }).catch(() => { /* don't escalate the error chain */ })
  }, [error])

  return (
    <html>
      <body style={{
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        padding: '2rem',
        backgroundColor: '#fef2f2',
        color: '#7f1d1d',
        minHeight: '100vh',
      }}>
        <div style={{
          maxWidth: '720px',
          margin: '0 auto',
          background: 'white',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#991b1b' }}>
            Something went wrong on this page
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '1rem' }}>
            The error has been logged. You can try the action again or reload the page.
          </p>

          {error.digest && (
            <p style={{ fontSize: '0.75rem', fontFamily: 'ui-monospace, monospace', color: '#9ca3af', marginBottom: '0.5rem' }}>
              Error reference: {error.digest}
            </p>
          )}

          {error.message && (
            <details style={{ marginTop: '1rem', fontSize: '0.75rem' }}>
              <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Technical detail</summary>
              <pre style={{
                marginTop: '0.5rem',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.75rem',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'ui-monospace, monospace',
                fontSize: '11px',
                color: '#374151',
              }}>{error.message}{error.stack ? `\n\n${error.stack}` : ''}</pre>
            </details>
          )}

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={reset}
              style={{
                padding: '0.5rem 1rem',
                background: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => { if (typeof window !== 'undefined') window.location.reload() }}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
